import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Flame, Footprints } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function WelcomeModal({ open, onClose, onAddHabit }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12141C] border border-emerald-500/30 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="welcome-modal">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center glow-emerald mb-3">
            <Sparkles className="w-7 h-7 text-black" />
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Welcome to SMART Momentum 👋</DialogTitle>
          <DialogDescription className="text-white">
            <span className="text-emerald-300 font-semibold">S</span>mall{" "}
            <span className="text-emerald-300 font-semibold">M</span>eaningful{" "}
            <span className="text-emerald-300 font-semibold">A</span>ctions{" "}
            <span className="text-emerald-300 font-semibold">R</span>epeated{" "}
            <span className="text-emerald-300 font-semibold">T</span>oday = Momentum.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 -mt-1">
          <Sparkles className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <p className="m-0 text-sm text-white">
            Enjoy everything free for <span className="font-bold text-emerald-300">7 days</span>. We'll only ask you to
            subscribe once your week is up — so you keep everything you've built.
          </p>
        </div>

        <div className="space-y-4 text-sm leading-relaxed text-white mt-1">
          <p>
            Here's the truth: <span className="text-emerald-400 font-semibold">the streak isn't the goal — the habit is.</span> One
            missed day means almost nothing. What actually derails people is missing <em>twice</em>.
          </p>

          <div className="flex items-start gap-3 p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
            <Flame className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
            <p className="m-0">
              So your only real rule is <span className="font-bold text-orange-300">"never miss twice."</span> That's it. That's the whole game.
            </p>
          </div>

          <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <Footprints className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <div className="m-0">
              <p className="m-0 mb-2">Shrink your habit until it feels almost embarrassingly small:</p>
              <ul className="m-0 space-y-1 list-none">
                <li>Not "exercise 30 minutes" → <span className="font-semibold">"put on your shoes."</span></li>
                <li>Not "meditate 10 minutes" → <span className="font-semibold">"take 3 deep breaths."</span></li>
              </ul>
            </div>
          </div>

          <p>
            When it's that tiny, you almost can't fail — and that builds the identity of someone who <span className="text-emerald-400 font-semibold">shows up</span>,
            which is far more powerful than any streak number.
          </p>

          <p className="text-white">
            So — what's the habit you keep breaking? Let's rebuild it together, stupidly small, anchored to something you already do every day.
            We'll make it stick this time.
          </p>
        </div>

        <div className="mt-6">
          <button
            data-testid="welcome-start-button"
            onClick={onClose}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Sparkles className="w-5 h-5" /> Let's build Momentum!
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
